import { useContext, useState, useEffect, useRef } from 'react';
import handleChange from '../../../utils/handleChange';
import './login.css';
import { genOTPFun, loginFun, reSendOtp } from '../../../api/userLoginAPI';
import { ReactComponent as CloseIcon } from '../../../assets/icons/trvto_close.svg';
import { UserAuthContext } from '../../../context/UserAuthContext';
import { toast } from 'react-toastify';
import errorHandle from '../../../utils/errorHandle';
import OTPInput from '../../otpinput/otpinput';
import phoneValid from '../../../utils/phoneValid';

const RESEND_DURATION = 60; // seconds
const OTP_EXPIRY_DURATION = 3 * 60 * 1000; // 3 minutes in ms

function Login({ setLogButton, setLoginSec, setLoading, loading }) {
    const { login } = useContext(UserAuthContext);

    const [userData, setUserData] = useState({ phoneNumber: '', otp: '' });
    const [phoneError, setPhoneError] = useState('');
    const [otpError, setOtpError] = useState('');

    // 'initial' | 'sent'
    const [otpState, setOtpState] = useState('initial');
    const [otpExpired, setOtpExpired] = useState(false);
    const [resendTimer, setResendTimer] = useState(0);
    const [otpResetKey, setOtpResetKey] = useState(0);
    // Refs so closures always see the latest values
    const resendIntervalRef = useRef(null);
    const otpExpiryTimeoutRef = useRef(null);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            clearInterval(resendIntervalRef.current);
            clearTimeout(otpExpiryTimeoutRef.current);
        };
    }, []);

    function stopAllTimers() {
        clearInterval(resendIntervalRef.current);
        resendIntervalRef.current = null;
        clearTimeout(otpExpiryTimeoutRef.current);
        otpExpiryTimeoutRef.current = null;
        setResendTimer(0);
    }

    function startResendTimer(duration = RESEND_DURATION) {
        clearInterval(resendIntervalRef.current);
        setResendTimer(duration);

        resendIntervalRef.current = setInterval(() => {
            setResendTimer((prev) => {
                if (prev <= 1) {
                    clearInterval(resendIntervalRef.current);
                    resendIntervalRef.current = null;
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
    }

    function scheduleOtpExpiry() {
        clearTimeout(otpExpiryTimeoutRef.current);

        otpExpiryTimeoutRef.current = setTimeout(() => {
            clearInterval(resendIntervalRef.current);
            resendIntervalRef.current = null;
            setResendTimer(0);

            setOtpExpired(true);
            setOtpState('initial'); // hide OTP input
            setOtpError('OTP has expired. Please generate a new one.');
        }, OTP_EXPIRY_DURATION);
    }

    async function sendOTP(method) {
        if (!phoneValid(userData.phoneNumber)) {
            setPhoneError('Please enter a valid phone number');
            return;
        }

        setPhoneError('');
        setLoading(true);

        try {
            if (method === 'resend') {
                await reSendOtp({ phoneNumber: userData.phoneNumber }, 'login', 'phone');
            } else {
                await genOTPFun({ phoneNumber: userData.phoneNumber });
            }

            toast.success('OTP sent to your WhatsApp');
            setOtpState('sent');
            setOtpExpired(false);
            setOtpError('');
            // Clear OTP field on resend
            setUserData((prev) => ({
                ...prev,
                otp: ''
            }));

            // Reset OTPInput boxes
            setOtpResetKey((prev) => prev + 1);

            startResendTimer(RESEND_DURATION);
            scheduleOtpExpiry();
        } catch (error) {
            errorHandle(error);
        } finally {
            setLoading(false);
        }
    }

    async function handleLogin() {
        if (userData.otp.length < 6) {
            const msg = 'Please enter the complete OTP';
            toast.error(msg);
            setOtpError(msg);
            return;
        }

        setOtpError('');
        setLoading(true);

        try {
            const loginData = await loginFun(userData);
            toast.success('Login Successful');
            stopAllTimers();
            login(loginData.data);
            setLogButton(false);
        } catch (error) {
            errorHandle(error);
        } finally {
            setLoading(false);
        }
    }

    const handleKeyDown = (e) => {
        if (e.key !== 'Enter') return;

        if (otpState === 'initial' || otpExpired) {
            sendOTP();
        } else {
            handleLogin();
        }
    };

    const handleMainButtonClick = () => {
        if (otpState === 'initial' || otpExpired) {
            sendOTP('login');
        } else {
            handleLogin();
        }
    };

    const formatTimer = (seconds) => {
        const m = Math.floor(seconds / 60);
        const s = String(seconds % 60).padStart(2, '0');
        return `${m}:${s}`;
    };

    const mainButtonLabel =
        otpState === 'initial' ? 'Send One Time Password' : otpExpired ? 'Generate New OTP' : 'Login';

    return (
        <div className='login'>
            <h1 className='loginH1'>{otpState === 'initial' ? 'Login' : 'OTP Verification'}</h1>
            <button type='button' onClick={() => setLogButton(false)} className='loginCancel'>
                <CloseIcon className='loginCloseIcon' />
            </button>

            {otpState === 'initial' && (
                <div className='loginInputDiv'>
                    <label>Phone Number (WhatsApp)</label>
                    <input
                        placeholder='Number'
                        type='text'
                        name='phoneNumber'
                        onChange={(e) => handleChange(e, setUserData)}
                        onKeyDown={handleKeyDown}
                        value={userData.phoneNumber}
                        className='regInput'
                    />
                    {phoneError && <span className='orgLoginError'>{phoneError}</span>}
                </div>
            )}

            {otpState === 'sent' && (
                <div className='loginInputDiv'>
                    <p>Check Messages (WhatsApp) for your OTP</p>
                    <label>OTP</label>
                    <OTPInput
                        length={6}
                        setUserData={setUserData}
                        keyName='otp'
                        handleKeyDown={handleKeyDown}
                        resetKey={otpResetKey}
                    />
                </div>
            )}

            {otpState === 'sent' && !otpExpired && (
                <div className='resend-container'>
                    {resendTimer > 0 ? (
                        <p className='resend-timer'>Resend OTP in {formatTimer(resendTimer)}</p>
                    ) : (
                        <button className='resend-btn' onClick={() => sendOTP('resend')} disabled={loading}>
                            Resend OTP
                        </button>
                    )}
                </div>
            )}

            {otpError && <span className='orgLoginError'>{otpError}</span>}

            <button
                type='button'
                onClick={handleMainButtonClick}
                className='loginBtn'
                disabled={userData.phoneNumber.length !== 10 || loading}
            >
                {mainButtonLabel}
            </button>

            <h2 onClick={() => setLoginSec('register')} className='loginForgot'>
                Sign Up
            </h2>
        </div>
    );
}

export default Login;
